import { ArrowLeft, Play } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useAnalysisToolNames,
  useCreateAnalysisRun,
} from '@/hooks/useAnalysis';
import { cn } from '@/lib/utils';
import type { AnalysisTargetType, UserAudience } from '@/types/analysis';

interface CreateAnalysisRunProps {
  onBack: () => void;
  onCreated: (runId: string) => void;
}

type ModelOption = {
  value: string;
  label: string;
  inputPer1M: number;
  outputPer1M: number;
};

type AnalysisTargetSelection = AnalysisTargetType | 'message_pairs';

// Pricing in USD per 1M tokens. Source: backend/lb/features/payment/payment_pricing_router.py
const MODEL_OPTIONS: ModelOption[] = [
  {
    value: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash Lite',
    inputPer1M: 0.25,
    outputPer1M: 1.5,
  },
  {
    value: 'gemini-3.5-flash-lite',
    label: 'Gemini 3.5 Flash Lite',
    inputPer1M: 0.3,
    outputPer1M: 2.5,
  },
  {
    value: 'gemini-3.6-flash',
    label: 'Gemini 3.6 Flash',
    inputPer1M: 1.5,
    outputPer1M: 7.5,
  },
  {
    value: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro (preview)',
    inputPer1M: 2,
    outputPer1M: 12,
  },
  {
    value: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    inputPer1M: 1,
    outputPer1M: 6,
  },
  { value: 'gpt-5.6', label: 'GPT-5.6', inputPer1M: 5, outputPer1M: 30 },
  {
    value: 'claude-sonnet-5',
    label: 'Claude Sonnet 5',
    inputPer1M: 3,
    outputPer1M: 15,
  },
  {
    value: 'claude-opus-5',
    label: 'Claude Opus 5',
    inputPer1M: 5,
    outputPer1M: 25,
  },
  {
    value: 'moonshotai/Kimi-K2.6-deepinfra',
    label: 'Kimi K2.6',
    inputPer1M: 0.75,
    outputPer1M: 3.5,
  },
  {
    value: 'moonshotai/kimi-k3-openrouter',
    label: 'Kimi K3',
    inputPer1M: 3,
    outputPer1M: 15,
  },
  {
    value: 'zai-org/GLM-5.2-baseten',
    label: 'GLM 5.2',
    inputPer1M: 1.5,
    outputPer1M: 4.5,
  },
];

// Mirrors backend DEFAULT_ANALYSIS_MESSAGES — used when the user leaves the limit field empty.
const DEFAULT_MESSAGE_LIMIT = 10_000;
// Mirrors backend DEFAULT_ANALYSIS_USERS.
const DEFAULT_USER_LIMIT = 1_000;
// Calibrated against real Axiom data: 1000-call runs averaged ~250 input / ~60 output tokens per message.
const TOKENS_IN_PER_MESSAGE = 250;
const TOKENS_OUT_PER_MESSAGE = 60;
const TOKENS_IN_PER_MESSAGE_PAIR = 1_000;
// User runs feed N activity summaries per user into one LLM call — rough per-summary estimate.
const TOKENS_IN_PER_SUMMARY = 600;
const TOKENS_OUT_PER_USER = 100;
const DEFAULT_SUMMARIES_PER_USER = 15;
const COST_WARNING_THRESHOLD_USD = 25;

const PROMPT_PLACEHOLDERS: Record<AnalysisTargetSelection, string> = {
  messages: '{message_text}',
  message_pairs: '{message_pair}',
  users: '{daily_summaries}',
  routines: '{routine}',
};

const PROMPT_EXAMPLES: Record<AnalysisTargetSelection, string> = {
  messages:
    'Analyze the following message and classify its sentiment:\n\n{message_text}\n\nRespond with JSON: {"sentiment": "positive"|"negative"|"neutral"}',
  message_pairs:
    'Analyze this user request and its corresponding assistant response:\n\n{message_pair}\n\nDetermine whether the assistant created a persistent artifact or only returned chat text. Respond with JSON: {"category": "artifact created"|"chat text only"|"failed"|"unclear"}',
  users:
    'Here are recent daily activity summaries for a user:\n\n{daily_summaries}\n\nEstimate the user\'s age bracket from behavioral signals. Use "not_inferable" liberally — do not guess.\n\nRespond with JSON: {"category": "18-24"|"25-34"|"35-44"|"45-54"|"55+"|"not_inferable"}',
  routines:
    'Here is a user routine:\n\n{routine}\n\nIt runs {schedule} with notifications {notifications} and auto-pause {auto_pause}.\n\nClassify what the routine is for.\n\nRespond with JSON: {"category": "productivity"|"news"|"health"|"finance"|"learning"|"other"}',
};

export default function CreateAnalysisRun({
  onBack,
  onCreated,
}: CreateAnalysisRunProps) {
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('gemini-3.1-flash-lite');
  const [targetType, setTargetType] =
    useState<AnalysisTargetSelection>('messages');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [role, setRole] = useState('');
  const [agent, setAgent] = useState('');
  const [assistantToolName, setAssistantToolName] = useState('');
  const [plans, setPlans] = useState('');
  const [itemLimit, setItemLimit] = useState('');
  const [signupFrom, setSignupFrom] = useState('');
  const [signupTo, setSignupTo] = useState('');
  const [activeSince, setActiveSince] = useState('');
  const [minSummaries, setMinSummaries] = useState('');
  const [summariesPerUser, setSummariesPerUser] = useState('');
  const [excludeDefault, setExcludeDefault] = useState(true);
  const [userAudience, setUserAudience] = useState<UserAudience>('external');

  const createRun = useCreateAnalysisRun();
  const toast = useToast();

  const isUserRun = targetType === 'users';
  const isRoutineRun = targetType === 'routines';
  const isMessagePairRun = targetType === 'message_pairs';
  const itemNoun = isUserRun
    ? 'users'
    : isRoutineRun
      ? 'routines'
      : isMessagePairRun
        ? 'message pairs'
        : 'messages';
  const { data: toolNames = [], isLoading: toolNamesLoading } =
    useAnalysisToolNames(isMessagePairRun);
  const requiredPlaceholder = PROMPT_PLACEHOLDERS[targetType];
  const promptValid = prompt.includes(requiredPlaceholder);

  const selectedModel =
    MODEL_OPTIONS.find((m) => m.value === model) ?? MODEL_OPTIONS[0];
  const parsedLimit = parseInt(itemLimit, 10);
  const estimatedItems =
    Number.isFinite(parsedLimit) && parsedLimit > 0
      ? parsedLimit
      : isUserRun
        ? DEFAULT_USER_LIMIT
        : DEFAULT_MESSAGE_LIMIT;
  const parsedSummariesPerUser = parseInt(summariesPerUser, 10);
  const effectiveSummariesPerUser =
    Number.isFinite(parsedSummariesPerUser) && parsedSummariesPerUser > 0
      ? parsedSummariesPerUser
      : DEFAULT_SUMMARIES_PER_USER;
  const tokensInPerItem = isUserRun
    ? effectiveSummariesPerUser * TOKENS_IN_PER_SUMMARY
    : isMessagePairRun
      ? TOKENS_IN_PER_MESSAGE_PAIR
      : TOKENS_IN_PER_MESSAGE;
  const tokensOutPerItem = isUserRun
    ? TOKENS_OUT_PER_USER
    : TOKENS_OUT_PER_MESSAGE;
  const estimatedCostUsd =
    (estimatedItems *
      (tokensInPerItem * selectedModel.inputPer1M +
        tokensOutPerItem * selectedModel.outputPer1M)) /
    1_000_000;
  const costWarning = estimatedCostUsd > COST_WARNING_THRESHOLD_USD;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !prompt.trim() || !promptValid) return;

    try {
      const filters: Record<string, unknown> = {};
      if (plans.trim())
        filters.plans = plans
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean);
      if (itemLimit) filters.limit = parseInt(itemLimit);
      filters.user_audience = userAudience;

      if (isUserRun) {
        if (signupFrom) filters.signup_from = signupFrom;
        if (signupTo) filters.signup_to = signupTo;
        if (activeSince) filters.active_since = activeSince;
        if (minSummaries) filters.min_summaries = parseInt(minSummaries);
        if (summariesPerUser)
          filters.summaries_per_user = parseInt(summariesPerUser);
      } else if (isRoutineRun) {
        if (dateFrom) filters.date_from = dateFrom;
        if (dateTo) filters.date_to = dateTo;
        filters.exclude_default = excludeDefault;
      } else {
        if (dateFrom) filters.date_from = dateFrom;
        if (dateTo) filters.date_to = dateTo;
        if (isMessagePairRun) {
          filters.message_mode = 'pairwise';
          if (assistantToolName)
            filters.assistant_tool_name = assistantToolName;
        } else if (role) filters.role = role;
        if (agent.trim()) filters.agent = agent.trim();
      }

      const result = await createRun.mutateAsync({
        name: name.trim(),
        prompt: prompt.trim(),
        model,
        target_type: isMessagePairRun ? 'messages' : targetType,
        filters,
      });

      toast.success('Analysis run created');
      onCreated(result.id);
    } catch (error) {
      toast.error(
        'Failed to create analysis run',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Runs
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Analysis Run</CardTitle>
          <CardDescription>
            Create a batch LLM analysis over chat messages, users, or routines
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 mb-6">
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
              <p className="font-semibold">Warning: This is expensive to run</p>
              <p className="text-sm mt-1">
                Each run makes multiple LLM calls. Please use responsibly and
                apply filters to limit the number of messages processed.
              </p>
            </div>
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
              <p className="font-semibold">
                Privacy: Do not extract personal data
              </p>
              <p className="text-sm mt-1">
                Do not craft prompts that extract or return personally
                identifiable information or private user content.
              </p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/40 text-foreground px-4 py-3 rounded-md">
              <p className="font-semibold">BAA-signed users are excluded</p>
              <p className="text-sm mt-1 text-muted-foreground">
                Users who have signed a BAA are automatically excluded from
                analysis sampling. Their messages, summaries, and routines will
                not be processed.
              </p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">What to analyze</label>
              <Select
                value={targetType}
                onChange={(e) =>
                  setTargetType(e.target.value as AnalysisTargetSelection)
                }
              >
                <option value="messages">Chat messages</option>
                <option value="message_pairs">
                  User + assistant message pairs
                </option>
                <option value="users">Users (via daily summaries)</option>
                <option value="routines">Routines</option>
              </Select>
              {isMessagePairRun && (
                <p className="text-sm text-muted-foreground">
                  Each user message is analyzed with every assistant message in
                  its turn, ending before the next user message. Tool names and
                  presented-file counts and types are included; filenames, tool
                  arguments, and file IDs are not.
                </p>
              )}
              {isUserRun && (
                <p className="text-sm text-muted-foreground">
                  Each user&apos;s recent activity summaries are combined and
                  analyzed in a single LLM call. Output a <code>category</code>{' '}
                  key to get a distribution in the run summary.
                </p>
              )}
              {isRoutineRun && (
                <p className="text-sm text-muted-foreground">
                  Each routine (a report template&apos;s title and prompt) is
                  analyzed as one item. Output a <code>category</code> key to
                  get a distribution in the run summary. Add{' '}
                  <code>{'{schedule}'}</code>, <code>{'{agent_mode}'}</code>,{' '}
                  <code>{'{notifications}'}</code>,{' '}
                  <code>{'{auto_pause}'}</code> or <code>{'{is_paused}'}</code>{' '}
                  to your prompt to include that part of the routine&apos;s
                  config.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  isUserRun
                    ? 'e.g. User age brackets - June 2026'
                    : 'e.g. Sentiment analysis - March 2026'
                }
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Prompt *</label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                placeholder={PROMPT_EXAMPLES[targetType]}
                required
              />
              {prompt && !promptValid && (
                <p className="text-sm text-destructive">
                  Prompt must contain {requiredPlaceholder} placeholder
                </p>
              )}
              {!prompt && (
                <p className="text-sm text-muted-foreground">
                  Use {requiredPlaceholder} as placeholder for{' '}
                  {isUserRun
                    ? "the user's combined daily summaries"
                    : isRoutineRun
                      ? "the routine's title and prompt"
                      : isMessagePairRun
                        ? 'the user request and assistant response'
                        : 'the chat message content'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Model</label>
              <Select value={model} onChange={(e) => setModel(e.target.value)}>
                {MODEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <div
                className={cn(
                  'px-3 py-2 rounded-md border text-sm',
                  costWarning
                    ? 'bg-destructive/10 border-destructive text-destructive'
                    : 'bg-muted border-border text-foreground'
                )}
              >
                <p className="font-semibold">
                  Estimated cost: ~${estimatedCostUsd.toFixed(2)} for{' '}
                  {estimatedItems.toLocaleString()} {itemNoun} on{' '}
                  {selectedModel.label}
                </p>
                <p className="text-xs mt-1 opacity-80">
                  Assumes ~{tokensInPerItem.toLocaleString()} input + ~
                  {tokensOutPerItem} output tokens per{' '}
                  {isUserRun
                    ? `user (${effectiveSummariesPerUser} summaries)`
                    : isRoutineRun
                      ? 'routine'
                      : isMessagePairRun
                        ? 'message pair'
                        : 'message'}
                  . Actual cost varies with content length and output size.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Filters (optional)
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {isUserRun ? (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Signup From</label>
                      <Input
                        type="date"
                        value={signupFrom}
                        onChange={(e) => setSignupFrom(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Signup To</label>
                      <Input
                        type="date"
                        value={signupTo}
                        onChange={(e) => setSignupTo(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Active Since
                      </label>
                      <Input
                        type="date"
                        value={activeSince}
                        onChange={(e) => setActiveSince(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Only users with activity summaries since this date
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Min Summaries
                      </label>
                      <Input
                        type="number"
                        value={minSummaries}
                        onChange={(e) => setMinSummaries(e.target.value)}
                        placeholder="default 15"
                        min={1}
                        max={50}
                      />
                      <p className="text-xs text-muted-foreground">
                        Skip users with fewer summaries than this
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Summaries Per User
                      </label>
                      <Input
                        type="number"
                        value={summariesPerUser}
                        onChange={(e) => setSummariesPerUser(e.target.value)}
                        placeholder="default 15"
                        min={1}
                        max={50}
                      />
                      <p className="text-xs text-muted-foreground">
                        Most recent summaries fed to the LLM per user
                      </p>
                    </div>
                  </>
                ) : isRoutineRun ? (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date From</label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date To</label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Exclude Default Routines
                      </label>
                      <div className="flex items-center gap-2 h-9">
                        <Switch
                          checked={excludeDefault}
                          onCheckedChange={setExcludeDefault}
                        />
                        <span className="text-sm text-muted-foreground">
                          Skip system default templates
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date From</label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date To</label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                    {isMessagePairRun ? (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Assistant Tool Call
                        </label>
                        <Select
                          value={assistantToolName}
                          onChange={(e) => setAssistantToolName(e.target.value)}
                          disabled={toolNamesLoading}
                        >
                          <option value="">
                            {toolNamesLoading
                              ? 'Loading tools...'
                              : 'Any / no tool filter'}
                          </option>
                          {toolNames.map((toolName) => (
                            <option key={toolName} value={toolName}>
                              {toolName}
                            </option>
                          ))}
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Include only turns where the assistant called this
                          tool at least once. Choose run_sandbox for artifact
                          generation analysis.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Role</label>
                        <Select
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                        >
                          <option value="">Any</option>
                          <option value="user">User</option>
                          <option value="assistant">Assistant</option>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Agent</label>
                      <Input
                        value={agent}
                        onChange={(e) => setAgent(e.target.value)}
                        placeholder="e.g. chat-agent"
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Users</label>
                  <Select
                    value={userAudience}
                    onChange={(e) =>
                      setUserAudience(e.target.value as UserAudience)
                    }
                  >
                    <option value="external">External users only</option>
                    <option value="internal">Internal users only</option>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Internal users are the team (dev and admin access). Pick
                    them to try a prompt out before running it on real users.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Plans</label>
                  <Input
                    value={plans}
                    onChange={(e) => setPlans(e.target.value)}
                    placeholder="comma-separated, e.g. basic,plus_monthly"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {isUserRun
                      ? 'User Limit'
                      : isRoutineRun
                        ? 'Routine Limit'
                        : isMessagePairRun
                          ? 'Pair Limit'
                          : 'Message Limit'}
                  </label>
                  <Input
                    type="number"
                    value={itemLimit}
                    onChange={(e) => setItemLimit(e.target.value)}
                    placeholder="e.g. 1000"
                    min={1}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t">
              <Button
                type="submit"
                disabled={
                  createRun.isPending ||
                  !name.trim() ||
                  !prompt.trim() ||
                  !promptValid
                }
              >
                <Play className="h-4 w-4 mr-2" />
                {createRun.isPending ? 'Creating...' : 'Create & Start'}
              </Button>
              <Button type="button" variant="outline" onClick={onBack}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
